import React, { useState } from 'preact/compat'
import { useFormInput } from '../hooks/useFormInput'
import { FieldText } from './FieldText'
import { FieldBasicCheckbox } from './FieldBasicCheckbox'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { accountTranslations } from '../helpers/translations'

/**
 *
 * @param {{
 *  address?: Address,
 *  isEdit: boolean,
 *  handleToggle: () => void,
 *  onSubmitCallback:(event: React.JSX.TargetedSubmitEvent<HTMLFormElement) => Promise<void>
 *  handleAddressDeleteCallback : (id: string) => Promise<void>
 * }} param0
 */
export const FormAccountAddress = ({
  address,
  isEdit,
  handleToggle,
  onSubmitCallback,
  handleAddressDeleteCallback
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { formInputs, handleOnChange } = useFormInput({
    address_line_1: address?.address_line_1 ?? '',
    address_line_2: address?.address_line_2 ?? '',
    city: address?.city ?? '',
    country: address?.country ?? '',
    first_name: address?.meta?.first_name ?? '',
    last_name: address?.meta?.last_name ?? '',
    state: address?.state ?? '',
    postcode: address?.postcode ?? '',
    is_default: address?.is_default ?? false
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    await onSubmitCallback(e)
    setIsLoading(false)
  }

  return (
    <div className={'flex h-full flex-col justify-between overflow-x-auto'}>
      <div className={'h-full w-full flex-grow'}>
        <form onSubmit={handleSubmit} className={'flex h-full flex-col justify-between'}>
          <div className={'flex flex-col p-4'}>
            <FieldText
              name={formInputs.first_name.key}
              required={true}
              value={formInputs.first_name.value}
              placeholder={accountTranslations.form_placeholders.first_name}
              label={accountTranslations.form_labels.first_name}
              autoComplete="given-name"
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.last_name.key}
              required={true}
              value={formInputs.last_name.value}
              placeholder={accountTranslations.form_placeholders.last_name}
              label={accountTranslations.form_labels.last_name}
              autoComplete="family-name"
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.address_line_1.key}
              required={true}
              value={formInputs.address_line_1.value}
              placeholder={accountTranslations.form_placeholders.address_line_1}
              label={accountTranslations.form_labels.address_line_1}
              autoComplete="address-line1"
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.city.key}
              required={true}
              value={formInputs.city.value}
              placeholder={accountTranslations.form_placeholders.city}
              label={accountTranslations.form_labels.city}
              autoComplete="address-level2"
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.state.key}
              required={true}
              value={formInputs.state.value}
              placeholder={accountTranslations.form_placeholders.state}
              label={accountTranslations.form_labels.state}
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.postcode.key}
              required={true}
              value={formInputs.postcode.value}
              placeholder={accountTranslations.form_placeholders.postcode}
              label={accountTranslations.form_labels.postcode}
              autoComplete="postal-code"
              onChange={handleOnChange}
            />
            <FieldText
              name={formInputs.country.key}
              required={true}
              value={formInputs.country.value}
              placeholder={accountTranslations.form_placeholders.country}
              label={accountTranslations.form_labels.country}
              autoComplete="country"
              onChange={handleOnChange}
            />
            <FieldBasicCheckbox
              name={formInputs.is_default.key}
              value={!!formInputs.is_default.value}
              onChange={handleOnChange}
              label="Set as primary address"
            />
            {address?.id && (
              <>
                <button
                  onClick={async () => {
                    await handleAddressDeleteCallback(address.id)
                  }}
                  class={'btn btn-inline paragraph-md w-fit py-4 font-bold text-primary-sale underline'}
                >
                  Delete
                </button>
                <input type="hidden" name="id" value={address.id} />
              </>
            )}
          </div>
          <div className={'flex flex-row gap-4 bg-neutrals-base p-4'}>
            <button type="button" className={'btn btn-md btn-outline-black'} onClick={handleToggle}>
              Cancel
            </button>
            <button
              className={'btn btn-md btn-solid-black relative flex-grow overflow-hidden'}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? <LoaderSpin wrapperClass="bg-black" /> : <>{address?.id ? 'Edit' : 'Add'} Address</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
